import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import appIcon from '../../assets/app-icon.png';

import '../..//assets/fontAwesome';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import UserProfileMenu from '../common/UserProfileMenu';
import ThemeContext from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import useAdminStatus from '../../hooks/useAdminStatus';

const Sidebar = ({ isOpen, toggleSidebar, isMobile = false, onCloseMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useContext(ThemeContext) || {};
  const { credits, userProfile, logout } = useAuth();
  const { isAdmin } = useAdminStatus();
  const { t } = useTranslation('common');
  const isDark = theme === 'dark';

  const navLinks = [
    { label: t('nav.dashboard'), path: '/dashboard', icon: 'home' },
    { label: t('nav.upscale'), path: '/upscale', icon: 'film' },
    { label: t('nav.credits'), path: '/credits', icon: 'coins', activePaths: ['/credits', '/recharge-credits'] },
    ...(isAdmin ? [{ label: 'Images', path: '/image-generation', icon: 'image', isAdmin: true }] : []),
    // Admin link - only shown if user is admin
    ...(isAdmin ? [{ label: 'Admin', path: '/admin', icon: 'crown', isAdmin: true }] : []),
  ];

  const isMuzoPlus =
    (userProfile?.plan?.status === 'active' && userProfile?.plan?.type === 'pro') ||
    userProfile?.saas_usage?.subscription_active;

  const isActive = (paths) => {
    const normalizedPaths = Array.isArray(paths) ? paths : [paths];
    return normalizedPaths.includes(location.pathname);
  };

  const handleNavClick = (path) => {
    if (isMobile && onCloseMobile) {
      onCloseMobile();
    }
    navigate(path);
  };

  const handleSettingsClick = () => {
    if (isMobile && onCloseMobile) {
      onCloseMobile();
    }
    navigate('/settings');
  };

  const handleLogout = () => {
    if (isMobile && onCloseMobile) {
      onCloseMobile();
    }
    logout();
  };

  const baseLink =
    'px-3 py-2.5 flex items-center transition-colors duration-200 relative group rounded-xl';
  const activeClass = isDark
    ? 'bg-white/10 text-white'
    : 'bg-indigo-50 text-indigo-600';
  const inactiveClass = isDark
    ? 'text-slate-400 hover:text-white hover:bg-white/5'
    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100';
  const tooltipClass = isDark
    ? 'bg-slate-900/90 text-white border-white/20'
    : 'bg-white text-slate-800 border-slate-200';

  // Mobile Drawer Layout
  if (isMobile) {
    return (
      <div
        className={`h-full flex flex-col ${
          isDark
            ? 'bg-slate-900 text-white'
            : 'bg-white text-slate-800'
        }`}
      >
        {/* Header with close button */}
        <div className={`flex items-center justify-between px-4 py-4 border-b ${
          isDark ? 'border-white/10' : 'border-slate-200'
        }`}>
          <div className="flex items-center gap-3">
            <div className="relative w-8 h-8">
              <img src={appIcon} alt="Muzo Studio" className="w-full h-full object-contain" />
            </div>
            <span className={`text-xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Muzo<span className="text-indigo-500">Studio</span>
            </span>
          </div>
          <button
            onClick={onCloseMobile}
            className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors ${
              isDark
                ? 'hover:bg-white/10 text-slate-400 hover:text-white'
                : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
            }`}
            aria-label={t('actions.closeMenu')}
          >
            <FontAwesomeIcon icon="times" className="text-lg" />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 flex flex-col gap-1 px-3 py-4">
          {navLinks.map(({ label, path, icon, activePaths, isAdmin: isAdminLink }) => (
            <button
              key={path}
              onClick={() => handleNavClick(path)}
              className={`${baseLink} ${
                isActive(activePaths || path) 
                  ? (isAdminLink ? 'bg-amber-500/20 text-amber-500' : activeClass)
                  : (isAdminLink ? 'text-amber-500/70 hover:text-amber-500 hover:bg-amber-500/10' : inactiveClass)
              }`}
            >
              <FontAwesomeIcon icon={icon} className={`w-5 ${isAdminLink ? 'text-amber-500' : ''}`} />
              <span className="ml-3 font-medium">{label}</span>
              {isActive(activePaths || path) && (
                <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full ${isAdminLink ? 'bg-amber-500' : 'bg-indigo-500'}`} />
              )}
            </button>
          ))}
        </nav>

        {/* Bottom Section - User Info */}
        <div className={`mt-auto border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
          {/* User Profile Summary */}
          <div className={`px-4 py-4 ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
            <div className="flex items-start gap-3">
              {/* Avatar */}
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
                {(userProfile?.profile?.display_name?.[0] || userProfile?.email?.[0] || 'U').toUpperCase()}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {userProfile?.profile?.display_name || userProfile?.email || t('user.default')}
                </p>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                  <span className={`flex items-center gap-1 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <FontAwesomeIcon icon="coins" className="text-amber-400" />
                    {credits ?? 0} {t('credits.label')}
                  </span>
                  {isMuzoPlus && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 text-[10px] font-bold px-2 py-0.5">
                      <FontAwesomeIcon icon="bolt" className="text-[9px]" />
                      Muzo+
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="px-3 py-3 space-y-1">
            <button
              onClick={handleSettingsClick}
              className={`w-full ${baseLink} ${inactiveClass}`}
            >
              <FontAwesomeIcon icon="cog" className="w-5" />
              <span className="ml-3">{t('actions.settings')}</span>
            </button>
            <button
              onClick={toggleTheme}
              className={`w-full ${baseLink} ${inactiveClass}`}
            >
              <FontAwesomeIcon icon={isDark ? 'sun' : 'moon'} className="w-5" />
              <span className="ml-3">{isDark ? t('actions.lightMode') : t('actions.darkMode')}</span>
            </button>
            <button
              onClick={handleLogout}
              className={`w-full ${baseLink} text-rose-500 hover:bg-rose-500/10`}
            >
              <FontAwesomeIcon icon="power-off" className="w-5" />
              <span className="ml-3">{t('actions.logout')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop Sidebar Layout (unchanged)
  return (
    <div
      className={`h-screen flex flex-col justify-between transition-all duration-300 border-r z-40 ${
        isOpen ? 'w-56' : 'w-14'
      } fixed ${
        isDark
          ? 'bg-slate-900/95 text-white border-white/10'
          : 'bg-white/95 text-slate-800 border-slate-200 shadow-md'
      } backdrop-blur`}
    >
      <div>
        <div className={`flex items-center justify-between ${isOpen ? 'px-4 py-3' : 'p-2'} relative`}>
          <div className="flex items-center">
            <div className="relative w-8 h-8 mr-3">
              <img src={appIcon} alt="Muzo Studio" className="w-full h-full object-contain" />
            </div>
            {isOpen && (
              <span className={`text-xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Muzo<span className="text-indigo-500">Studio</span>
              </span>
            )}
          </div>
        </div>

        <nav className="flex flex-col gap-1 px-2">
          {navLinks.map(({ label, path, icon, activePaths, isAdmin: isAdminLink }) => (
            <Link
              key={path}
              to={path}
              className={`${baseLink} ${
                isActive(activePaths || path) 
                  ? (isAdminLink ? 'bg-amber-500/20 text-amber-500' : activeClass)
                  : (isAdminLink ? 'text-amber-500/70 hover:text-amber-500 hover:bg-amber-500/10' : inactiveClass)
              }`}
            >
              <FontAwesomeIcon icon={icon} className={isAdminLink ? 'text-amber-500' : ''} />
              <span
                className={`whitespace-nowrap transition-all duration-200 ${
                  isOpen ? 'opacity-100 max-w-[160px] ml-3' : 'opacity-0 max-w-0 ml-0'
                }`}
              >
                {label}
              </span>
              {!isOpen && (
                <span
                  className={`pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 text-xs whitespace-nowrap rounded border opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition ${isAdminLink ? 'bg-amber-500 text-white border-amber-400' : tooltipClass}`}
                >
                  {label}
                </span>
              )}
            </Link>
          ))}
        </nav>
      </div>

      <div className={`${isOpen ? 'px-3 pb-4' : 'px-2 pb-2'} flex flex-col items-center w-full`}>
        {isOpen && (
          <div className={`w-full h-px mb-3 ${isDark ? 'bg-white/10' : 'bg-slate-200'}`} />
        )}
        <div className="w-full max-w-full">
          {isOpen ? (
            <UserProfileMenu className="w-full" />
          ) : (
            /* Collapsed state: Credits above, Avatar with badge overlay */
            <div className="flex flex-col items-center gap-2">
              {/* Credits counter */}
              <div
                className={`flex items-center justify-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${
                  isDark
                    ? 'bg-white/10 text-white'
                    : 'bg-slate-100 text-slate-700'
                }`}
                title={`${credits ?? 0} ${t('credits.label')}`}
              >
                <FontAwesomeIcon icon="coins" className="text-amber-400 text-[10px]" />
                <span>{credits ?? 0}</span>
              </div>
              
              {/* Avatar with Muzo+ badge overlay */}
              <div className="relative">
                <UserProfileMenu compact className="w-full" />
                {isMuzoPlus && (
                  <div 
                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-center justify-center"
                    title="Muzo+"
                  >
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 shadow-lg ring-2 ring-slate-900">
                      <FontAwesomeIcon icon="bolt" className="text-[9px] text-slate-900" />
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toggle centered on sidebar edge */}
      <button
        className={`absolute right-[-16px] top-1/2 -translate-y-1/2 z-20 rounded-full w-8 h-8 flex items-center justify-center shadow-lg border ${
          isDark
            ? 'text-white bg-slate-900 border-white/20'
            : 'text-slate-700 bg-white border-slate-200'
        }`}
        onClick={toggleSidebar}
        title={isOpen ? t('actions.reduce') : t('actions.expand')}
      >
        <FontAwesomeIcon icon={isOpen ? 'arrow-left' : 'arrow-right'} />
      </button>
    </div>
  );
};

export default Sidebar;
